import { isMemoryArchiveType } from "./leaderMemoryVisibility.ts";
export type BehavioralGraphNodeLike = {
  node_type?: string;
  source_type: string;
  source_id: string;
  title: string | null;
  created_at: string | null;
  metadata?: Record<string, unknown> | null;
};

export type BehavioralGraphState = {
  canonicalThemes: string[];
  contradictionCount: number;
  contradictionSignals: string[];
  latestEvidenceAt: string | null;
  recency: "fresh" | "recent" | "stale" | "unknown";
  thin: boolean;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function parseTimestamp(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

export function normalizeBehavioralKey(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

export function toBehavioralLabel(value: string): string {
  return value
    .trim()
    .split(/[_-]+/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function compactBehavioralStrings(items: Array<string | null | undefined>, limit = 6): string[] {
  const seen = new Set<string>();
  return items
    .map((item) => (typeof item === "string" ? item.trim() : ""))
    .filter((item) => item.length > 0)
    .filter((item) => {
      if (seen.has(item)) return false;
      seen.add(item);
      return true;
    })
    .slice(0, limit);
}

export function truncateBehavioralEvidenceText(value: string, maxLength = 180): string {
  const normalized = value.replace(/\s+/g, " ").trim();
  if (normalized.length <= maxLength) return normalized;
  if (maxLength <= 3) return normalized.slice(0, maxLength);
  return `${normalized.slice(0, maxLength - 3).trimEnd()}...`;
}

function deriveRecency(latestEvidenceAt: string | null, referenceNowMs: number): BehavioralGraphState["recency"] {
  if (!latestEvidenceAt) return "unknown";

  const latestTs = new Date(latestEvidenceAt).getTime();
  if (Number.isNaN(latestTs)) return "unknown";

  const ageDays = (referenceNowMs - latestTs) / (1000 * 60 * 60 * 24);
  if (ageDays <= 14) return "fresh";
  if (ageDays <= 45) return "recent";
  return "stale";
}

function extractCanonicalTheme(node: BehavioralGraphNodeLike): string | null {
  const policy = asRecord(node.metadata);
  if (policy?.suppressed === true || ["rejected","retired","contradicted"].includes(String(policy?.status))
    || Number(policy?.contradiction_count ?? 0) > 0 || policy?.contradiction_flag === true) return null;
  if (
    node.source_type !== "behavioral_intelligence_v2_theme" &&
    node.source_type !== "behavioral_intelligence_v2_cluster"
  ) {
    return null;
  }

  const metadata = asRecord(node.metadata);
  const raw = typeof metadata?.behavior_theme === "string"
    ? metadata.behavior_theme
    : typeof metadata?.theme_name === "string"
      ? metadata.theme_name
      : node.title;

  if (typeof raw !== "string" || raw.trim().length === 0) return null;
  return toBehavioralLabel(raw);
}

function extractContradictionSignal(node: BehavioralGraphNodeLike): string | null {
  const metadata = asRecord(node.metadata);
  const raw = typeof metadata?.contradiction_signal === "string"
    ? metadata.contradiction_signal
    : typeof metadata?.contradiction_reason === "string"
      ? metadata.contradiction_reason
      : typeof metadata?.contradiction_summary === "string"
        ? metadata.contradiction_summary
        : null;

  return raw?.trim().length ? raw.trim() : null;
}

export function buildGraphBehavioralState(
  nodes: BehavioralGraphNodeLike[],
  referenceNow = Date.now(),
): BehavioralGraphState {
  nodes = nodes.filter(node => !isMemoryArchiveType(node.node_type) && !isMemoryArchiveType(node.source_type));
  const canonicalThemes = compactBehavioralStrings(
    nodes
      .map((node) => extractCanonicalTheme(node))
      .filter((theme): theme is string => Boolean(theme)),
    6,
  );

  const contradictionSignals = compactBehavioralStrings(
    nodes
      .map((node) => extractContradictionSignal(node))
      .filter((signal): signal is string => Boolean(signal)),
    6,
  );

  const contradictionCount = nodes.reduce((count, node) => {
    const metadata = asRecord(node.metadata);
    const rawCount = metadata?.contradiction_count;
    if (typeof rawCount === "number" && Number.isFinite(rawCount)) return count + Math.max(0, rawCount);
    if (typeof rawCount === "string") {
      const parsed = Number(rawCount);
      if (Number.isFinite(parsed)) return count + Math.max(0, parsed);
    }
    return extractContradictionSignal(node) ? count + 1 : count;
  }, 0);

  const latestEvidenceAt = nodes
    .map((node) => node.created_at)
    .filter((value): value is string => Boolean(value))
    .sort((left, right) => {
      const leftTs = parseTimestamp(left) ?? 0;
      const rightTs = parseTimestamp(right) ?? 0;
      return rightTs - leftTs;
    })[0] ?? null;

  const evidenceCount = nodes.length;
  const thin = canonicalThemes.length === 0 || evidenceCount <= 1;

  return {
    canonicalThemes,
    contradictionCount,
    contradictionSignals,
    latestEvidenceAt,
    recency: deriveRecency(latestEvidenceAt, referenceNow),
    thin,
  };
}
